/* Admin desk · KB approvals — review queue for client-profile change requests.
   Lists pending edits, shows the diff + rendered preview, and lets an admin
   approve (updates canonical), reject, or request changes. Reads/writes
   window.MAV.kbRequests / kbProfiles via decideKB. Reuses window.DiffView/MiniMD. */

import { decideKbProposal } from '../lib/kb-workflow.js';
import { isKbDialectProposal, kbProposalDetailMissing, kbDialectReview, kbDialectSelfReview, assertKbDialectDecision } from '../lib/kb-dialect-review.js';
import { KbDialectReview } from './kb-dialect-review.jsx';
const { useState: useKbAdminState } = React;

const KB_STATUS = {
  pending_review:    { pill: 'pill-cond', label: 'Pending review', ic: 'warn' },
  approved:          { pill: 'pill-pass', label: 'Approved', ic: 'dot' },
  rejected:          { pill: 'pill-fail', label: 'Rejected', ic: 'dot' },
  changes_requested: { pill: 'pill-run',  label: 'Changes requested', ic: 'dot' },
};

function KBStatusPill({ status }) {
  const s = KB_STATUS[status] || KB_STATUS.pending_review;
  return <span className={'pill ' + s.pill} style={{ flexShrink: 0 }}>{s.ic === 'warn' ? <I.warn size={11} /> : <span className="dot"></span>}{s.label}</span>;
}

function KAvatar({ m, size = 22 }) {
  if (!m) return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: 'var(--queued)' }}>·</span>;
  return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: m.color }}>{m.initials}</span>;
}

function kbReqRisk(req) {
  if (isKbDialectProposal(req)) return ['ESP dialect pack', 'Independent review'];
  var diff = (window.kbDiffLines || function () { return []; })(req.baseMarkdown ?? '', req.proposedMarkdown);
  return window.MAV.kbRiskTags(diff.filter(r => r.t === 'add').map(r => r.text).join('\n'));
}

function KBApprovals() {
  const MAV = window.MAV;
  const { DiffView, MiniMD } = window;
  const [, force] = useKbAdminState(0);
  const [openId, setOpenId] = useKbAdminState(null);
  const [filter, setFilter] = useKbAdminState('pending_review');
  const [note, setNote] = useKbAdminState('');
  const [previewProposed, setPreviewProposed] = useKbAdminState(false);
  const [decisionError, setDecisionError] = useKbAdminState('');
  const [deciding, setDeciding] = useKbAdminState(false);
  const [detailError, setDetailError] = useKbAdminState('');
  const contextKey = `${MAV.__contextKey || ''}:${openId || ''}`;
  const actionContext = React.useRef(null);
  const decisionEpoch = React.useRef(0);
  if (actionContext.current?.key !== contextKey) actionContext.current = { key: contextKey };
  React.useEffect(() => () => { actionContext.current = null; }, []);
  React.useEffect(() => { setDeciding(false); setDecisionError(''); setDetailError(''); setNote(''); }, [contextKey]);

  const reqs = MAV.kbRequests;
  const selected = reqs.find(r => r.id === openId);
  const detailMissing = kbProposalDetailMissing(selected);
  const pendingCount = reqs.filter(r => r.status === 'pending_review').length;

  // When a proposal opens, lazy-load what the (lite) hydrate left out: the proposed
  // markdown and the canonical profile body — both needed for the diff. Mutates the
  // existing req object + forces a re-render (this screen's force() pattern). No-ops
  // for mock data, which already carries proposedMarkdown.
  React.useEffect(() => {
    if (!openId || deciding) return;
    let alive = true;
    const r = (MAV.kbRequests || []).find(x => x.id === openId);
    const context = actionContext.current;
    const epoch = decisionEpoch.current;
    if (kbProposalDetailMissing(r) && window.GooseAPI && window.GooseAPI.kbProposal) {
      window.GooseAPI.kbProposal(openId)
        .then(resp => {
          if (!alive || decisionEpoch.current !== epoch || actionContext.current !== context || `${window.MAV.__contextKey || ''}:${openId}` !== context.key) return;
          if (resp?.proposal?._id !== r._id || resp.proposal.id !== r.id) throw Error('Proposal identity changed. Reopen the review.');
          const current = window.MAV.kbRequests.find(x => x._id === r._id && x.id === r.id);
          if (current) { Object.assign(current, resp.proposal); force(x => x + 1); }
        })
        .catch(e => { if (alive && actionContext.current === context) setDetailError(e.message || 'Could not load the proposal. Reopen to retry.'); });
    }
    const prof = r && !isKbDialectProposal(r) && MAV.kbProfiles[r.slug];
    if (prof && prof._stub && window.GooseLoadKbProfile) {
      window.GooseLoadKbProfile(r.slug).then(() => { if (alive) force(x => x + 1); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [contextKey, selected?._id, detailMissing, deciding]);

  /* ---- review detail ---- */
  if (openId) {
    const req = reqs.find(r => r.id === openId);
    if (!req) return <div role="status">This proposal is no longer available in this workspace. <button className="btn" onClick={() => setOpenId(null)}>Back to KB approvals</button></div>;
    const profile = MAV.kbProfiles[req.slug] || { name: req.slug };
    // Live proposals carry the proposer's name+initials directly; fall back to that
    // synthetic member when memberById can't resolve a real workspace id (mock uses
    // initials keys; live uses a server-provided name).
    const m = MAV.memberById(req.requester) || (req.requesterName ? { name: req.requesterName, initials: req.requester } : null);
    const risk = kbReqRisk(req);
    const decided = req.status !== 'pending_review';
    const dialect = isKbDialectProposal(req);
    const dialectReview = kbDialectReview(req, MAV.activeWorkspaceId);
    const selfReview = kbDialectSelfReview(req, MAV.currentUser);
    const detailReady = dialect ? dialectReview.valid : !kbProposalDetailMissing(req);
    const decide = async (d) => {
      if (deciding || !detailReady || d === 'approve' && selfReview) return;
      if ((d === 'reject' || d === 'changes') && !note.trim()) return;
      const key = actionContext.current;
      decisionEpoch.current++;
      setDeciding(true); setDecisionError('');
        try {
          const resp = await decideKbProposal(window.GooseAPI, req.id, d === 'changes' ? 'request_changes' : d, note);
          if (actionContext.current !== key || `${window.MAV.__contextKey || ''}:${req.id}` !== key.key) return;
          const p = resp && resp.proposal;
          assertKbDialectDecision(req, resp, MAV.activeWorkspaceId);
          const current = window.MAV.kbRequests.find(x => x._id === req._id && x.id === req.id);
          if (!current || p?._id !== req._id) throw Error('Proposal identity changed. Reload before retrying.');
          if (p) {
            Object.assign(current, p);
            if (!dialect && resp.approvedVersion && MAV.kbProfiles[req.slug]) {
              MAV.kbProfiles[req.slug].versionId = resp.approvedVersion;
              if (p.proposedMarkdown) MAV.kbProfiles[req.slug].approvedMarkdown = p.proposedMarkdown;
            }
          }
          setNote(''); force(x => x + 1);
          return;
        } catch (e) {
          if (actionContext.current === key) setDecisionError((e && e.message) || 'Could not record the decision.');
        } finally {
          if (actionContext.current === key) setDeciding(false);
        }
    };
    return (
      <div className="fade-in">
        <button className="tkt-back" onClick={() => { setOpenId(null); setPreviewProposed(false); }}><I.chevron size={15} style={{ transform: 'rotate(180deg)' }} /> Back to KB approvals</button>
        <div className="row between wrap gap-10" style={{ margin: '8px 0 4px', alignItems: 'flex-start' }}>
          <div>
            <div className="row gap-8" style={{ marginBottom: 6 }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{req.id}</span>
              <ClientBadge slug={req.slug} size={22} />
              <span className="mono muted" style={{ fontSize: 11.5, alignSelf: 'center' }}>{req.sourcePath}</span>
            </div>
            <h2 style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>{dialect ? `ESP dialect pack · ${req.espDialect?.submission?.esp_key || 'packet review'}` : `${profile.name} profile edit`}</h2>
          </div>
          <KBStatusPill status={req.status} />
        </div>

        <div className="kb-review-meta">
          <div className="row gap-8"><KAvatar m={m} size={24} /><span style={{ fontSize: 12.5 }}><b>{m ? m.name : req.requester}</b> · submitted {req.submittedAt}</span></div>
          <span className="muted mono" style={{ fontSize: 11.5 }}>base {req.baseVersion}</span>
          {req.linkedTicket && <span className="chip" style={{ fontSize: 10.5 }}><I.link size={11} /> {req.linkedTicket}</span>}
          {risk.map(r => <span key={r} className="chip pill-cond" style={{ fontSize: 10.5 }}><I.warn size={10} /> {r}</span>)}
        </div>

        <div className="card card-pad" style={{ margin: '14px 0' }}>
          <div className="sec-head" style={{ marginBottom: 8 }}><span className="sec-title" style={{ fontSize: 13.5 }}>Requester note</span></div>
          <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>{req.note || <span className="muted">No note provided.</span>}</p>
        </div>

        {dialect ? <KbDialectReview review={dialectReview} /> : <div className="card card-pad">
          <div className="sec-head">
            <span className="sec-title">{previewProposed ? 'Proposed profile · preview' : 'Diff · captured base → proposed'}</span>
            <div className="tabs"><span className={'tab' + (!previewProposed ? ' active' : '')} onClick={() => setPreviewProposed(false)}>Diff</span><span className={'tab' + (previewProposed ? ' active' : '')} onClick={() => setPreviewProposed(true)}>Preview</span></div>
          </div>
          {!req.proposedMarkdown
            ? <div className="muted" style={{ padding: 20, fontSize: 13 }}>Loading proposed changes…</div>
            : previewProposed
              ? <div className="kb-paper" style={{ maxHeight: 420, overflowY: 'auto' }}><MiniMD text={req.proposedMarkdown} /></div>
              : <DiffView approved={req.baseMarkdown ?? ''} proposed={req.proposedMarkdown} />}
        </div>}
        {decisionError && <div className="kb-banner fail" role="alert">{decisionError}</div>}
        {detailError && <div className="kb-banner fail" role="alert">{detailError}</div>}
        {!dialect && req.espPolicy && <div className="kb-banner cond">ESP policy change for {req.slug} / {req.espPolicy.esp}. Affects future integrations for this client, not other clients or existing builds. Requires an independent reviewer.</div>}
        {!dialect && req.espDefault && <div className="kb-banner cond">Default platform for {req.espDefault.scope==='variant'?`${req.slug} / ${req.espDefault.variant?.variant_id}`:req.slug}: {req.espDefault.target.esp.toUpperCase()} · {req.espDefault.target.esp_tier}. Only new builds capture this selection after approval and publication. Existing builds, tag rules and neutral approvals are unchanged. Explicit build choice wins over variant, then client default.</div>}

        {/* decision */}
        {decided ? (
          <div className={'kb-banner mt-16 ' + (req.status === 'approved' ? 'pass' : req.status === 'rejected' ? 'fail' : 'run')}>
            <b>{KB_STATUS[req.status].label}</b> by {req.reviewedBy} · {req.reviewedAt}.{req.adminNote ? ' “' + req.adminNote + '”' : ''}
            {!dialect && req.status === 'approved' && (!req.publication
              ? <> Publication receipt unavailable for this older approval.</>
              : req.publication.state === 'published'
              ? <> Approved version <b>{req.approvedVersionId}</b> is published.</>
              : <> Approval is recorded; publication is pending. <button className="btn btn-ghost" disabled={deciding || !detailReady} onClick={() => decide('approve')}>Retry publication</button></>)}
          </div>
        ) : (
          <div className="card card-pad mt-16">
            <div className="sec-head"><span className="sec-title">Decision</span><span className="muted" style={{ fontSize: 11.5 }}>Reject / request changes need a note</span></div>
            {selfReview && <div className="kb-banner cond">An independent reviewer must approve your dialect pack. The server enforces reviewer authorization.</div>}
            <textarea className="input" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Admin note (required to reject or request changes)…" style={{ resize: 'none', marginBottom: 12 }} />
            <div className="row gap-10 wrap">
              <button className="btn btn-primary btn-lg" disabled={deciding || !detailReady || selfReview} onClick={() => decide('approve')}><I.check size={16} /> {dialect ? 'Approve pack' : 'Approve & publish version'}</button>
              <button className="btn btn-ghost" onClick={() => decide('changes')} disabled={deciding || !detailReady || !note.trim()} title={note.trim() ? 'Send back to developer' : 'Add a note first'}><I.retry size={15} /> Request changes</button>
              <button className="btn btn-danger" onClick={() => decide('reject')} disabled={deciding || !detailReady || !note.trim()} title={note.trim() ? 'Reject' : 'Add a note first'}><I.x size={15} /> Reject</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ---- list ---- */
  const filters = [['pending_review', 'Pending', pendingCount], ['all', 'All', reqs.length], ['approved', 'Approved'], ['rejected', 'Rejected'], ['changes_requested', 'Changes']];
  const shown = filter === 'all' ? reqs : reqs.filter(r => r.status === filter);

  return (
    <div>
      <div className="row gap-8 wrap" style={{ marginBottom: 16 }}>
        {filters.map(([id, l, n]) => (
          <button key={id} className={'chip' + (filter === id ? ' chip-sel' : '')} onClick={() => setFilter(id)}
            style={filter === id ? { background: 'var(--ink)', color: 'var(--surface)' } : { cursor: 'pointer' }}>{l}{n != null ? ' · ' + n : ''}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ color: 'var(--pass)', marginBottom: 8 }}><I.check size={26} /></div>
          <p style={{ fontWeight: 700, margin: 0 }}>Nothing here</p>
          <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>No {filter === 'all' ? '' : KB_STATUS[filter] ? KB_STATUS[filter].label.toLowerCase() + ' ' : ''}KB change requests.</p>
        </div>
      ) : (
        <div className="card card-pad">
          {shown.map(req => {
            const m = MAV.memberById(req.requester);
            const risk = kbReqRisk(req);
            return (
              <button key={req.id} className="kb-req-row" onClick={() => setOpenId(req.id)}>
                <ClientBadge slug={req.slug} size={34} />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row between wrap gap-8">
                    <div className="row gap-8" style={{ minWidth: 0 }}>
                      <span className="mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{req.id}</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{isKbDialectProposal(req) ? `ESP dialect pack · ${req.espDialect?.submission?.esp_key || req.slug}` : MAV.kbProfiles[req.slug]?.name || req.slug}</span>
                      <span className="muted mono" style={{ fontSize: 11 }}>{req.sourcePath}</span>
                    </div>
                    <KBStatusPill status={req.status} />
                  </div>
                  <div className="kb-req-sum">{req.note}</div>
                  <div className="row between wrap gap-8" style={{ marginTop: 9, alignItems: 'center' }}>
                    <div className="row gap-8" style={{ alignItems: 'center' }}>
                      <KAvatar m={m} size={20} />
                      <span className="muted" style={{ fontSize: 11.5 }}>{m ? m.name : req.requester} · {req.submittedAt}</span>
                    </div>
                    <div className="row gap-6 wrap">
                      {req.linkedTicket && <span className="chip" style={{ fontSize: 10 }}><I.link size={10} /> {req.linkedTicket}</span>}
                      {risk.map(r => <span key={r} className="chip pill-cond" style={{ fontSize: 10 }}>{r}</span>)}
                      <span className="kb-open">Review <I.chevron size={13} /></span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

window.KBApprovals = KBApprovals;
